import type { Locale } from "@/config/locale";
import { selectUnitLabel, type UnitLabel } from "@/lib/unit-label";

/** Spanish label, shared with the regional `es-mx`. */
const DAYS_LABEL_ES: UnitLabel = "días";

// Every storefront locale needs an entry: a missing one would fall back to English
// for both the word and the number format ("~6.5 days").
const DAYS_LABEL: Record<Locale, UnitLabel> = {
	de: "Tage",
	en: "days",
	bg: "дни",
	cs: "dnů",
	da: "dage",
	el: "ημέρες",
	es: DAYS_LABEL_ES,
	"es-mx": DAYS_LABEL_ES,
	fi: "päivää",
	fr: "jours",
	hr: "dana",
	sl: "dni",
	sr: "dana",
	bs: "dana",
	mk: "дена",
	lt: { one: "diena", few: "dienos", many: "dienos", other: "dienų" },
	lv: "dienas",
	et: "päeva",
	hu: "nap",
	it: "giorni",
	nb: "dager",
	nl: "dagen",
	pl: "dni",
	pt: "dias",
	"pt-br": "dias",
	ro: "zile",
	sk: "dní",
	sv: "dagar",
	tr: "gün",
};

/**
 * Localized half-life display for a peptide.
 *
 * - hours >= 48: shown in days, preserving one meaningful decimal
 * - hours >= 1: shown as "~N h" and preserves one meaningful decimal
 * - hours <  1: shown as "~N min" (minimum one minute)
 * - undefined: returns the caller's localized fallback or an empty string
 */
export function formatHalfLifeHours(
	hours: number | undefined,
	locale: Locale | string,
	fallback = "",
): string {
	if (typeof hours !== "number" || !Number.isFinite(hours) || hours <= 0) {
		return fallback;
	}
	const displayLocale = Object.hasOwn(DAYS_LABEL, locale) ? locale : "en";
	const numberFormat = new Intl.NumberFormat(displayLocale, {
		maximumFractionDigits: 1,
		useGrouping: false,
	});
	if (hours >= 48) {
		// The plural form follows the displayed (rounded) value, not the raw quotient.
		const days = Math.round((hours / 24) * 10) / 10;
		const label = selectUnitLabel(DAYS_LABEL[displayLocale as Locale], displayLocale, days);
		return `~${numberFormat.format(days)} ${label}`;
	}
	if (hours >= 1) {
		return `~${numberFormat.format(hours)} h`;
	}
	const minutes = Math.max(1, Math.round(hours * 60));
	return `~${minutes} min`;
}
